import React from "react";
import "./LayoutPreview.css";

type Element = { x: number; y: number; width?: number; height?: number; fontSize?: number; color?: string | null };

type Props = {
	/** Board canvas size the coordinates are in. */
	canvasWidth: number;
	canvasHeight: number;
	/** Background image behind the elements. */
	background?: string;
	/** Fallback colour for text elements with no colour of their own. */
	textColor?: string;
	/** Elements to draw, in render order. */
	elements: { key: string; label: string; text?: boolean; sample?: string; el: Element }[];
	/** Widest the preview may be; it scales to fit. */
	maxWidth?: number;
	/** Highlight this element (the row being edited). */
	activeKey?: string | null;
};

/**
 * A scale model of the board canvas showing where each element sits. Text
 * elements are centre-anchored and drawn at their real (scaled) font size;
 * images are top-left boxes. It's a positioning aid, not a pixel-accurate
 * render of the board — the board itself is the source of truth.
 */
export class LayoutPreview extends React.Component<Props, { boxWidth: number }> {
	observer: ResizeObserver | null = null;
	host = React.createRef<HTMLDivElement>();

	constructor(props: Props) {
		super(props);
		this.state = { boxWidth: 0 };
	}

	componentDidMount() {
		// Fit the space available rather than a fixed width, so the preview
		// works on a narrow column as well as a wide one.
		this.observer = new ResizeObserver((entries) => {
			const width = entries[0]?.contentRect?.width || 0;
			if (width && Math.abs(width - this.state.boxWidth) > 1) this.setState({ boxWidth: width });
		});
		if (this.host.current) this.observer.observe(this.host.current);
	}

	componentWillUnmount() {
		this.observer?.disconnect();
	}

	render() {
		const { canvasWidth, canvasHeight, background, textColor, elements, activeKey } = this.props;
		const maxWidth = this.props.maxWidth || 460;
		const width = Math.max(1, canvasWidth || 576);
		const height = Math.max(1, canvasHeight || 432);
		const available = Math.min(maxWidth, this.state.boxWidth || maxWidth);
		const scale = available / width;

		return (
			<div className="lp-host" ref={this.host}>
			<div className="lp-preview-wrap" style={{ height: height * scale, width: width * scale }}>
				<div
					className="lp-preview"
					style={{
						backgroundImage: background ? `url(${background})` : undefined,
						height,
						transform: `scale(${scale})`,
						width
					}}
				>
					{elements.map(({ key, label, text, sample, el }) => {
						if (!el) return null;
						const active = key === activeKey;
						return text
							? <div
								className={`lp-el lp-el-text${active ? " active" : ""}`}
								key={key}
								style={{ color: el.color || textColor || "#FFFFFF", fontSize: el.fontSize, left: el.x, top: el.y }}
								title={label}
							>
								{sample || label}
							</div>
							: <div
								className={`lp-el lp-el-box${active ? " active" : ""}`}
								key={key}
								style={{ height: el.height, left: el.x, top: el.y, width: el.width }}
								title={label}
							>
								<span>{label}</span>
							</div>;
					})}
				</div>
			</div>
			</div>
		);
	}
}
